// src/pages/Articulos/ActualizadorArticulos.js
//
// Actualización masiva de artículos desde Excel.
//
// El flujo es en cuatro pasos, y el tercero es el que importa: antes de escribir
// se muestra qué cambiaría, con el valor actual al lado del nuevo. Actualizar el
// maestro en masa no tiene deshacer, y el mismo archivo que corrige 200
// artículos los rompe si trae una columna corrida.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import { parseExcelFile } from '../../utils/excelParser';
import { useToast } from '../../components/Toast/ToastProvider';
import { formatNumber } from '../../utils/formato';
import './ActualizadorArticulos.css';

/** Igual que la del backend (articulosCampos.js): saca acentos y símbolos. */
const normalizar = (t) => String(t ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();

const ActualizadorArticulos = () => {
  const { showToast } = useToast();

  const [definicion, setDefinicion] = useState(null);   // campos y alias, del backend
  const [archivo, setArchivo] = useState(null);
  const [filasExcel, setFilasExcel] = useState([]);
  const [columnaCodigo, setColumnaCodigo] = useState(null);
  const [detectados, setDetectados] = useState([]);     // campos presentes en el Excel
  const [seleccionados, setSeleccionados] = useState([]);
  const [previsualizacion, setPrevisualizacion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    api.get('/articulos/campos-actualizables')
      .then(({ data }) => setDefinicion(data))
      .catch(() => showToast('No se pudo leer la lista de campos actualizables.', 'danger'));
  }, [showToast]);

  /**
   * Arma y descarga la plantilla.
   *
   * Los encabezados salen del backend (campo `encabezado`), que garantiza que
   * el detector los reconoce: si se escribieran a mano acá, alcanzaría con una
   * tilde de diferencia para que el importador no encuentre la columna.
   *
   * Incluye la hoja de categorías válidas porque el campo guarda un código y no
   * el nombre: sin la lista a mano, se inventan nombres que no existen.
   */
  const descargarPlantilla = async () => {
    if (!definicion) return;
    try {
      const { data: categorias } = await api.get('/articulos/categorias');

      const encabezados = [definicion.encabezadoCodigo, ...definicion.campos.map(c => c.encabezado)];
      const ejemplo = {};
      encabezados.forEach(h => { ejemplo[h] = ''; });
      ejemplo[definicion.encabezadoCodigo] = 'C000948';

      const wb = XLSX.utils.book_new();

      const hoja = XLSX.utils.json_to_sheet([ejemplo], { header: encabezados });
      hoja['!cols'] = encabezados.map(h => ({ wch: Math.max(16, h.length + 2) }));
      XLSX.utils.book_append_sheet(wb, hoja, 'Articulos');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['Cómo usar esta plantilla'],
        [],
        ['1. Completá una fila por artículo. La única columna obligatoria es el código.'],
        ['2. Borrá las columnas que no vayas a modificar, o dejalas vacías.'],
        [],
        ['IMPORTANTE: una celda vacía significa "no tocar ese campo".'],
        ['No borra el dato que ya tiene el artículo en SAP.'],
        [],
        ['3. Subí el archivo al portal y elegí qué campos aplicar.'],
        ['4. Antes de escribir vas a ver qué cambia en cada artículo.'],
        [],
        ['Categoría: podés poner el código (002) o el nombre (ACCESORIOS VEHICULARES).'],
        ['La lista completa está en la hoja "Categorias".'],
        [],
        ['Los encabezados de esta plantilla son los que el importador reconoce.'],
        ['Si los cambiás, puede que no encuentre la columna.']
      ]), 'Instrucciones');

      const hojaCat = XLSX.utils.json_to_sheet(
        categorias.map(c => ({ 'Código': c.codigo, 'Nombre': c.nombre })));
      hojaCat['!cols'] = [{ wch: 10 }, { wch: 42 }];
      XLSX.utils.book_append_sheet(wb, hojaCat, 'Categorias');

      XLSX.writeFile(wb, 'Plantilla actualizacion de articulos.xlsx');
      showToast('Plantilla descargada.', 'success');
    } catch (err) {
      showToast('No se pudo generar la plantilla.', 'danger');
    }
  };

  const reiniciar = () => {
    setArchivo(null); setFilasExcel([]); setColumnaCodigo(null);
    setDetectados([]); setSeleccionados([]); setPrevisualizacion(null); setResultado(null);
  };

  /** Lee el Excel y detecta qué columnas corresponden a campos conocidos. */
  const handleArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !definicion) return;
    reiniciar();
    setArchivo(file);

    try {
      const filas = await parseExcelFile(file);
      if (filas.length === 0) { showToast('El archivo está vacío.', 'warning'); return; }

      const encabezados = Object.keys(filas[0]);
      const indice = {};
      encabezados.forEach(h => { indice[normalizar(h)] = h; });

      // Columna del código de artículo: sin ella no hay contra qué actualizar.
      const colCodigo = definicion.aliasCodigo.map(a => indice[a]).find(Boolean);
      if (!colCodigo) {
        showToast('No se encontró la columna del código de artículo (ItemCode).', 'danger', 9000);
        return;
      }

      // Sólo se ofrecen los campos que realmente vienen en el archivo.
      const encontrados = definicion.campos
        .map(c => ({ ...c, columna: c.alias.map(a => indice[a]).find(Boolean) }))
        .filter(c => c.columna);

      if (encontrados.length === 0) {
        showToast('El archivo no tiene ninguna columna reconocible para actualizar.', 'warning', 9000);
        return;
      }

      setFilasExcel(filas);
      setColumnaCodigo(colCodigo);
      setDetectados(encontrados);
      setSeleccionados(encontrados.map(c => c.campo)); // por defecto, todos los detectados
    } catch (err) {
      showToast(err.message || 'No se pudo leer el archivo.', 'danger', 9000);
    }
  };

  // --- Selección de campos ---
  const todosSeleccionados = detectados.length > 0 && seleccionados.length === detectados.length;
  const algunoSeleccionado = seleccionados.length > 0 && !todosSeleccionados;

  // `indeterminate` no existe como atributo HTML, sólo como propiedad del DOM:
  // por eso hay que tocarlo por ref y no se puede pasar como prop.
  const refTodos = useRef(null);
  useEffect(() => {
    if (refTodos.current) refTodos.current.indeterminate = algunoSeleccionado;
  }, [algunoSeleccionado]);

  const alternarTodos = () => {
    setPrevisualizacion(null);
    setSeleccionados(todosSeleccionados ? [] : detectados.map(c => c.campo));
  };

  const alternar = (campo) => {
    setPrevisualizacion(null);
    setSeleccionados(s => s.includes(campo) ? s.filter(c => c !== campo) : [...s, campo]);
  };

  /** Arma el payload que entienden /comparar y /actualizar. */
  const armarPayload = useCallback(() => ({
    campos: seleccionados,
    filas: filasExcel.map(f => {
      const valores = {};
      detectados
        .filter(c => seleccionados.includes(c.campo))
        .forEach(c => { valores[c.campo] = f[c.columna]; });
      return { codigo: f[columnaCodigo], valores };
    })
  }), [filasExcel, detectados, seleccionados, columnaCodigo]);

  const previsualizar = async () => {
    setCargando(true); setResultado(null);
    try {
      const { data } = await api.post('/articulos/comparar', armarPayload());
      setPrevisualizacion(data);
    } catch (err) {
      showToast(err.response?.data?.error || 'No se pudo generar la previsualización.', 'danger', 9000);
    } finally {
      setCargando(false);
    }
  };

  const aplicar = async () => {
    setAplicando(true);
    try {
      const { data } = await api.post('/articulos/actualizar', armarPayload());
      setResultado(data);
      setPrevisualizacion(null);
      showToast(`${data.actualizados} artículos actualizados.`, 'success', 7000);
    } catch (err) {
      showToast(err.response?.data?.error || 'No se pudieron actualizar los artículos.', 'danger', 9000);
    } finally {
      setAplicando(false);
    }
  };

  const ETIQUETA_ESTADO = {
    'ok':          { texto: 'Se actualiza', clase: 'text-bg-primary' },
    'sin-cambios': { texto: 'Sin cambios',  clase: 'text-bg-secondary' },
    'inexistente': { texto: 'No existe',    clase: 'text-bg-warning' },
    'error':       { texto: 'Error',        clase: 'text-bg-danger' }
  };

  return (
    <div className="container-fluid actualizador">
      <h1 className="mt-4 mb-1">
        <i className="bi bi-pencil-square me-2"></i>
        Actualización Masiva de Artículos
      </h1>
      <p className="text-muted">
        Subí un Excel con el código de artículo y las columnas que quieras modificar.
        Antes de escribir vas a ver exactamente qué cambia.
      </p>

      {/* --- Paso 1: archivo --- */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h5 className="card-title mb-0"><span className="paso">1</span> Archivo</h5>
            <button className="btn btn-outline-primary btn-sm" onClick={descargarPlantilla} disabled={!definicion}>
              <i className="bi bi-download me-1"></i>Descargar plantilla
            </button>
          </div>
          <input
            type="file"
            className="form-control"
            accept=".xlsx,.xls,.csv"
            onChange={handleArchivo}
            disabled={!definicion}
          />
          {archivo && filasExcel.length > 0 && (
            <p className="text-muted small mb-0 mt-2">
              <i className="bi bi-file-earmark-spreadsheet me-1"></i>
              {archivo.name} · {formatNumber(filasExcel.length)} filas ·
              código en la columna <code>{columnaCodigo}</code>
            </p>
          )}
          {definicion && (
            <details className="mt-2">
              <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                Columnas que reconoce el importador
              </summary>
              <ul className="small text-muted mt-2 mb-0">
                <li><strong>Código:</strong> {definicion.aliasCodigo.join(', ')}</li>
                {definicion.campos.map(c => (
                  <li key={c.campo}><strong>{c.etiqueta}:</strong> {c.alias.join(', ')}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>

      {/* --- Paso 2: campos --- */}
      {detectados.length > 0 && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h5 className="card-title"><span className="paso">2</span> Campos a actualizar</h5>
            <p className="text-muted small">
              Sólo se listan los que están en el archivo. Una celda vacía deja ese
              campo como está: no lo borra.
            </p>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 pb-2 border-bottom">
              <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer' }}>
                <input
                  ref={refTodos}
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={todosSeleccionados}
                  onChange={alternarTodos}
                />
                <span className="fw-semibold">
                  {todosSeleccionados ? 'Destildar todos' : 'Seleccionar todos'}
                </span>
              </label>
              <small className="text-muted">
                {seleccionados.length} de {detectados.length} seleccionados
              </small>
            </div>

            <div className="row g-2">
              {detectados.map(c => (
                <div className="col-md-6 col-lg-4" key={c.campo}>
                  <label className={`campo-check ${seleccionados.includes(c.campo) ? 'activo' : ''}`}>
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={seleccionados.includes(c.campo)}
                      onChange={() => alternar(c.campo)}
                    />
                    <span>
                      {c.etiqueta}
                      <small className="d-block text-muted">columna «{c.columna}»</small>
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <button
              className="btn btn-primary mt-3"
              onClick={previsualizar}
              disabled={cargando || seleccionados.length === 0}
            >
              {cargando
                ? <><span className="spinner-border spinner-border-sm me-2" />Comparando con SAP...</>
                : <><i className="bi bi-search me-1" />Ver qué cambia</>}
            </button>
          </div>
        </div>
      )}

      {/* --- Paso 3: previsualización --- */}
      {previsualizacion && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h5 className="card-title"><span className="paso">3</span> Previsualización</h5>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className="chip-resumen text-bg-primary">{previsualizacion.resumen.aActualizar} se actualizan</span>
              <span className="chip-resumen text-bg-secondary">{previsualizacion.resumen.sinCambios} sin cambios</span>
              {previsualizacion.resumen.inexistentes > 0 &&
                <span className="chip-resumen text-bg-warning">{previsualizacion.resumen.inexistentes} no existen</span>}
              {previsualizacion.resumen.errores > 0 &&
                <span className="chip-resumen text-bg-danger">{previsualizacion.resumen.errores} con error</span>}
            </div>

            <div className="tabla-preview">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '110px' }}>Estado</th>
                    <th style={{ width: '96px' }}>Código</th>
                    <th>Artículo</th>
                    <th>Cambios</th>
                  </tr>
                </thead>
                <tbody>
                  {previsualizacion.filas.map((f, i) => {
                    const et = ETIQUETA_ESTADO[f.estado] || ETIQUETA_ESTADO.error;
                    return (
                      <tr key={`${f.codigo}-${i}`} className={f.estado === 'sin-cambios' ? 'opacity-50' : ''}>
                        <td><span className={`badge ${et.clase}`}>{et.texto}</span></td>
                        <td><code className="small">{f.codigo}</code></td>
                        <td className="small">{f.nombre || '—'}</td>
                        <td className="small">
                          {f.motivo && <span className="text-danger">{f.motivo}</span>}
                          {(f.cambios || []).map(c => (
                            <div key={c.campo} className="linea-cambio">
                              <span className="text-muted">{c.etiqueta}:</span>{' '}
                              <span className="valor-antes">{String(c.antesTexto ?? '(vacío)')}</span>
                              <i className="bi bi-arrow-right mx-1 text-muted"></i>
                              <span className="valor-despues">{String(c.despuesTexto)}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
              <button
                className="btn btn-success"
                onClick={aplicar}
                disabled={aplicando || previsualizacion.resumen.aActualizar === 0}
              >
                {aplicando
                  ? <><span className="spinner-border spinner-border-sm me-2" />Actualizando...</>
                  : <><i className="bi bi-check2-circle me-1" />Aplicar {previsualizacion.resumen.aActualizar} cambios en SAP</>}
              </button>
              <button className="btn btn-outline-secondary" onClick={() => setPrevisualizacion(null)} disabled={aplicando}>
                Cancelar
              </button>
              <span className="text-muted small ms-auto">
                Sólo se escriben las filas marcadas «Se actualiza».
              </span>
            </div>
          </div>
        </div>
      )}

      {/* --- Paso 4: resultado --- */}
      {resultado && (
        <div className="card shadow-sm">
          <div className="card-body">
            <h5 className="card-title"><span className="paso">4</span> Resultado</h5>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className="chip-resumen text-bg-success">{resultado.actualizados} actualizados</span>
              <span className="chip-resumen text-bg-secondary">{resultado.sinCambios} sin cambios</span>
              {resultado.inexistentes > 0 && <span className="chip-resumen text-bg-warning">{resultado.inexistentes} no existen</span>}
              {resultado.conError > 0 && <span className="chip-resumen text-bg-danger">{resultado.conError} con error</span>}
            </div>
            {resultado.errores?.length > 0 && (
              <div className="tabla-preview">
                <table className="table table-sm mb-0">
                  <thead><tr><th>Código</th><th>Error de SAP</th></tr></thead>
                  <tbody>
                    {resultado.errores.map(e => (
                      <tr key={e.codigo}><td><code className="small">{e.codigo}</code></td><td className="small text-danger">{e.error}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button className="btn btn-outline-primary mt-3" onClick={reiniciar}>
              <i className="bi bi-arrow-repeat me-1"></i>Cargar otro archivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActualizadorArticulos;
